import React, {useState, useEffect, useRef} from 'react';
import {StyledElement} from './StyledTooltip';
import {useSelector, useDispatch} from 'react-redux';
import {labels, currentResource} from '../../../util/utils';
import {getFlattenedContactsFromEntities, getUniqueContacts} from '../../../util/contactsDB';
import OutputText from '@ey/canvascoreservices/OutputText';
import ColorLabelTag from '../ColorLabelTag';
import {setTags} from '../../../actions/ContactDB';
import {raiseJSValidationError} from '../../../actions/erroractions';
import Form from '@ey/canvascoreservices/Form';
import {
	contactPropertiesOperation,
	tags,
	userIsNotEntityAdminOfAllSelectedEntitiesErrorCodeCustomAttributesContacts,
	newUserIsNotEntityAdminOfAllSelectedEntitiesErrorCodeCustomAttributesContacts,
	contactHasBeenDeletedErrorCodeCustomAttributes,
	newContactHasBeenDeletedErrorCodeCustomAttributes
} from '../../../util/uiconstants';
import HrAlt from '../Hr/HrAlt';
import LoadingIndicator from '../LoadingIndicator';
import normalizeTagSelection from './normalizeTagSelection';
import {updateDisplayData} from '../../../actions/Metrics/dashboardContextAction';
import getUnadministrableUsers from '../../../components/Pages/ContactDB/Toolbar/DefineCustomAttribute/DCAForm/utils/getUnadministrableUsers';

const TagList = (props) => {
	const {selectedTags, checkbox = false, tagsList} = props;
	const TAG_LIST = 'tagsList';
	const [isDisabled, setIsDisabled] = useState(null);
	const projectId = useSelector((store) => store.project?.projectId);
	const engagements = useSelector(({dashboardContext}) => dashboardContext?.engagements);
	const entities = useSelector(({dashboardContext}) => dashboardContext?.entities);
	const orgChart = useSelector(({dashboardContext}) => dashboardContext?.orgChart);
	const userGui = useSelector(({userPreferences}) => userPreferences?.gui);
	const isProjectOwner = useSelector(
		({project}) => !!project?.users?.find(({userId}) => userId === userGui)?.isProjectOwner
	);
	const selectedContacts = useSelector(({contacts}) => contacts?.selected);
	const dispatch = useDispatch();

	const initialValues = {
		[TAG_LIST]: tagsList
	};
	const schema = {
		[TAG_LIST]: {required: true}
	};

	const isEqual = (tagsListCompare, formValues) => {
		return JSON.stringify(tagsListCompare) === JSON.stringify(formValues);
	};

	const isEntityAdmin = (entityId, userGui) => {
		const entity = entities?.find((x) => x.entityId === entityId);
		return entity?.entityAdmins?.some((x) => x.userId === userGui);
	};

	const renderTags = (tagList, handleSubmit) => {
		const tags = tagList
			.filter((t) => !t.isCustom)
			.map((tag) => {
				const tagName =
					currentResource.tags.find((t) => t.id?.toLowerCase() === tag?.key?.toLowerCase())?.name || tag?.name;

				return (
					<ColorLabelTag
						isDisabled={isDisabled}
						checkbox
						tagsSelected={selectedTags?.tags?.[tag.id]}
						key={tag.id}
						wrapperClass="span-separator"
						entity={selectedTags}
						name={tagName}
						id={tag.id}
						onSubmit={handleSubmit}
						colorName={tag.color.toLowerCase()}
					/>
				);
			});
		return tags;
	};

	const renderCustomTags = (tagList, handleSubmit) => {
		const customTags = tagList
			.filter((t) => t.isCustom)
			.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));

		if (!customTags.length > 0) return false;

		return (
			<>
				<HrAlt />
				<OutputText title={labels.customTagsHeader}>{labels.customTagsHeader}</OutputText>
				{customTags.map((tag) => {
					return (
						<ColorLabelTag
							isDisabled={isDisabled}
							checkbox
							tagsSelected={selectedTags?.tags?.[tag.id]}
							key={tag.id}
							wrapperClass="span-separator"
							entity={selectedTags}
							name={tag.name}
							id={tag.id}
							onSubmit={handleSubmit}
							colorName={tag.color.toLowerCase()}
						/>
					);
				})}
			</>
		);
	};

	const handleAllContactsNotEditable = (users) => {
		const userList = getUniqueContacts(getFlattenedContactsFromEntities(entities))
			.filter(({userId}) => users.includes(userId))
			.map(({name, lastName}) => `${name} ${lastName}`)
			.sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()))
			.join(', ');
		const errorCode = userIsNotEntityAdminOfAllSelectedEntitiesErrorCodeCustomAttributesContacts;
		dispatch(
			raiseJSValidationError(
				`<strong>${errorCode}:</strong> ${currentResource.Errors[errorCode].replace(
					'{0}',
					`<strong>${userList}</strong>`
				)}`
			)
		);
	};

	const displayCustomError = (error) => {
		const errorCode = error?.response?.data?.errors?.Error?.[0]?.split('-')?.[0].trim() || '';
		if (errorCode === userIsNotEntityAdminOfAllSelectedEntitiesErrorCodeCustomAttributesContacts) {
			const errorContacts = error?.response?.data?.errors?.Error[0]?.split(/[\-\:]/)[2] || '';
			const users = userListErrorMessage(entities, displayedEntities, userGui);
			const resouceLabel = currentResource.Errors[errorCode];
			let userList = [];
			users?.forEach((user) => userList.push(`${user.name} ${user.lastName}`));
			userList = userList.sort();
			let names = '';
			userList?.forEach((user, i) => {
				if (i === userList.length - 1) {
					names = names + user;
				} else {
					names = names + user + ', ';
				}
			});
			dispatch(
				raiseJSValidationError(
					`<strong>${newUserIsNotEntityAdminOfAllSelectedEntitiesErrorCodeCustomAttributesContacts}:</strong> ${resouceLabel.replace(
						'{0}',
						errorContacts
					)}`
				)
			);
		}
		if (errorCode === contactHasBeenDeletedErrorCodeCustomAttributes) {
			const errorContacts = error?.response?.data?.errors?.Error[0]?.split(/[\-\:]/)[2] || '';
			const resouceLabel = currentResource.Errors[errorCode];
			dispatch(
				raiseJSValidationError(
					`<strong>${newContactHasBeenDeletedErrorCodeCustomAttributes}:</strong> ${resouceLabel.replace(
						'{0}',
						errorContacts
					)}`
				)
			);
		}
	};

	const formProps = {
		initialValues,
		schema,
		onSubmit: async (values, callback) => {
			let valid = true;

			const unadministrableUsers = getUnadministrableUsers(selectedContacts, userGui, entities);
			if (valid && !isProjectOwner && unadministrableUsers.length > 0) {
				handleAllContactsNotEditable(unadministrableUsers);
				setIsLoading(false);
				valid = false;
			}

			if (valid) {
				const entitiesSelected = callback.entity;
				const api = [];
				setIsDisabled(true);
				const tagModified = tagsList?.find((tag) => tag.id === callback.id);
				const operation = !callback.checked ? contactPropertiesOperation.addTag : contactPropertiesOperation.removeTag;
				let newEntities = [...entities];

				if (isProjectOwner) {
					Object.keys(entitiesSelected).forEach((entity) => {
						if (entity !== tags) {
							const selectedUser = new Set(entitiesSelected[entity]);
							const payload = {userIds: Array.from(selectedUser), tagModified, operation};
							api.push(() =>
								dispatch(setTags(projectId, selectedUser, tagModified, operation, entity))
									.then(() => {
										newEntities = normalizeTagSelection(newEntities, payload);
										dispatch(updateDisplayData(engagements, newEntities, orgChart, true));
									})
									.catch((error) => {
										displayCustomError(error);
										callback();
									})
							);
						}
					});
				} else {
					Object.keys(entitiesSelected).forEach((entityId) => {
						if (isEntityAdmin(entityId, userGui)) {
							if (entityId !== tags) {
								const selectedUser = new Set(entitiesSelected[entityId]);
								const payload = {userIds: Array.from(selectedUser), tagModified, operation};
								api.push(() =>
									dispatch(setTags(projectId, selectedUser, tagModified, operation, entityId))
										.then(() => {
											newEntities = normalizeTagSelection(newEntities, payload);
											dispatch(updateDisplayData(engagements, newEntities, orgChart, true));
										})
										.catch((error) => {
											displayCustomError(error);
											callback();
										})
								);
							}
						}
					});
				}

				const results = [];
				for (const call of api) results.push(await Promise.allSettled([call()]));
				setIsDisabled(false);
			}
		}
	};

	return (
		<>
			<OutputText title={labels.tagFilter} ellipsis>
				{labels.tagFilter}
			</OutputText>
			<HrAlt />
			{tagsList ? (
				<>
					<OutputText title={labels.keyContacts} ellipsis>
						{labels.keyContacts}
					</OutputText>
					<Form {...formProps}>
						{({values, errors, handleChange, handleSubmit, handleReset, isFormValid}) => {
							if (!isEqual(tagsList, values[TAG_LIST])) {
								handleChange(TAG_LIST, tagsList);
							}
							return (
								<>
									{renderTags(values[TAG_LIST], handleSubmit)}
									{renderCustomTags(values[TAG_LIST], handleSubmit)}
								</>
							);
						}}
					</Form>
				</>
			) : (
				<LoadingIndicator show />
			)}
		</>
	);
};
export default TagList;
